import React, { useEffect, useState } from "react";
import "./App.scss";
import NavBar from "./Components/NavBar/NavBar";
import Home from "./Components/Home/Home";
import About from "./Components/About/About";
import Skills from "./Components/Skills/Skills";
// import Projects from "./Components/Projects/Projects";
import Contact from "./Components/Contact/Contact";
import Footer from "./Components/Footer/Footer";
import ProjectsGallery from "./Components/ProjectsGallery/ProjectsGallery"; // Import the ProjectsGallery component

import AceBakeryImg from "./assets/Images/acebakery.png";
import Atkinson from "./assets/Images/atkinson.png";
import valorHockey from "./assets/Images/valorhockey.png";
import stonefire from "./assets/Images/stonefire.png";
import ChoosingWisely from "./assets/Images/choosingwisely.png";
import DrugChecking from "./assets/Images/drugchecking.png";
import Shopify from "./assets/Images/shopify.png";
import GardenCulture from "./assets/Images/gardenculture.png";
import RootsLandingPage from "./assets/Images/RootsLandingPage1.webp";
import RootsReferAFriend from "./assets/Images/RootsReferaFriend.webp";
import TenFour from "./assets/Images/TenFour.webp";

function App() {
  const [nightMode, setNightMode] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    document.body.classList.toggle("is-night-mode", nightMode);

    return () => {
      document.body.classList.remove("is-night-mode");
    };
  }, [nightMode]);

  useEffect(() => {
    const revealElements = Array.from(document.querySelectorAll(".reveal"));

    if (!revealElements.length) {
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      revealElements.forEach((element) => element.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: "0px 0px -48px 0px",
        threshold: 0.16,
      }
    );

    revealElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let animationFrame = null;

    const updateScrollProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress =
        scrollableHeight > 0 ? (scrollTop / scrollableHeight) * 100 : 0;

      setScrollProgress(Math.min(Math.max(progress, 0), 100));
      animationFrame = null;
    };

    const handleScroll = () => {
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(updateScrollProgress);
      }
    };

    updateScrollProgress();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  const projectData = [
    {
      id: "roots-ecommerce",
      group: "featured",
      img: RootsLandingPage,
      title: "Roots E-commerce Experiences",
      cardInfo:
        "Responsive campaign landing pages, shoppable editorial modules and product-focused experiences for Roots' Salesforce Commerce Cloud site.",
      info: "Production front-end development for Roots' e-commerce site, including responsive campaign landing pages, editorial shopping experiences, shoppable product tiles, influencer/lookbook pages, product merchandising modules, accessibility checks, QA support and Salesforce Commerce Cloud content work.",
      overview:
        "Production front-end development across campaign, editorial, and merchandising experiences for Roots' e-commerce site.",
      role: "Front-end development, responsive layout implementation, Salesforce Commerce Cloud content work, QA support, accessibility checks, and launch coordination.",
      highlights: [
        "Built seasonal campaign landing pages used for paid media and product discovery.",
        "Developed editorial shopping experiences with influencer content, embedded video, carousels and shoppable product tiles.",
        "Implemented responsive layouts across desktop and mobile using campaign creative and merchandising requirements.",
        "Supported QA, accessibility checks and launch coordination across staging and production.",
        "Worked within Salesforce Commerce Cloud content assets and Page Designer.",
      ],
      techStack: [
        "Salesforce Commerce Cloud",
        "HTML",
        "CSS",
        "JavaScript",
        "Page Designer",
        "Responsive Design",
        "E-commerce",
      ],
    },
    {
      id: "roots-refer-a-friend",
      group: "featured",
      img: RootsReferAFriend,
      title: "Roots Refer a Friend / Bloomreach",
      cardInfo:
        "A multi-step referral modal flow built with Bloomreach web layers, custom JavaScript, audience logic and production QA.",
      info: "A custom multi-step referral experience built with Bloomreach web layers, replacing a third-party referral journey with an on-site modal flow.",
      overview:
        "A custom multi-step referral experience built with Bloomreach web layers, replacing a third-party referral journey with an on-site modal flow.",
      role: "Built and tested the web layer UI, step logic, CTA injection, audience setup support, campaign ID migration, and production QA.",
      highlights: [
        "Built and tested a multi-step Refer a Friend experience using Bloomreach web layers.",
        "Implemented custom modal UI, CTA injection and event-driven step transitions.",
        "Worked with audience conditions, consent considerations and production QA.",
        "Supported dev-to-prod migration of Bloomreach campaign/web layer IDs.",
        "Helped support English and French versions of the flow.",
      ],
      techStack: [
        "Bloomreach",
        "JavaScript",
        "Web Layers",
        "A/B Testing",
        "Personalization",
        "SFCC",
        "QA",
      ],
    },
    {
      id: "ten-four-crimefax",
      group: "featured",
      img: TenFour,
      title: "Ten Four / Crimefax",
      cardInfo:
        "Product and front-end work for a public safety data platform using live and historical crime data.",
      info: "Product and front-end work for a public safety data platform that aggregates live and historical crime data for Canadian regions, with use cases across real estate, policing, government, hotels and community safety.",
      overview:
        "Product and front-end work for a public safety data platform that aggregates live and historical crime data for Canadian regions.",
      role: "Contributed to product experience, front-end implementation, API-connected flows, and use-case framing for public safety data users.",
      highlights: [
        "Worked on product surfaces for live and historical crime data exploration.",
        "Supported workflows for real estate, policing, government, hotels and community safety use cases.",
        "Helped shape front-end interactions around data clarity, scanning and decision-making.",
      ],
      techStack: ["React", "Node", "API", "Product", "Startup", "Public Safety Data"],
    },
    {
      id: "valor-hockey",
      group: "featured",
      img: valorHockey,
      title: "Valor Hockey Shopify Storefront",
      cardInfo:
        "Shopify storefront and product page customization for a hockey helmet brand.",
      info: "Shopify storefront development for a hockey helmet brand, including product page customization, responsive styling, Liquid templating, product configuration UI and e-commerce UX improvements.",
      overview:
        "Shopify storefront development for a hockey helmet brand focused on product education, configuration and purchase confidence.",
      role: "Implemented Shopify theme updates, Liquid templates, responsive styling, product page customizations and storefront UX improvements.",
      highlights: [
        "Customized product page templates and merchandising areas.",
        "Built responsive styling across product and content sections.",
        "Worked with Liquid, HTML, CSS and jQuery to support store behavior.",
      ],
      techStack: ["Shopify", "Liquid", "HTML", "CSS", "jQuery", "E-commerce"],
      link: "https://valorhockey.com/en-ca",
    },
    {
      id: "independent-shopify",
      group: "future",
      status: "Coming Soon",
      img: Shopify,
      title: "Independent Shopify Storefront",
      cardInfo:
        "A self-directed Shopify theme build exploring merchandising, landing pages and conversion-focused UX.",
      info: "A self-directed Shopify build exploring theme customization, product merchandising, landing pages, conversion-focused UX and lightweight brand identity.",
      overview:
        "A self-directed Shopify build exploring theme customization, product merchandising, landing pages, conversion-focused UX and lightweight brand identity.",
      role: "Planning and building the storefront as a focused Shopify theme development exercise.",
      highlights: [
        "Theme customization and product merchandising.",
        "Landing page patterns for campaign and product discovery.",
        "Conversion-focused UX decisions with lightweight brand identity.",
      ],
      techStack: ["Shopify", "Liquid", "Theme Development", "E-commerce"],
    },
    {
      id: "atkinson-foundation",
      group: "earlier",
      img: Atkinson,
      title: "Atkinson Foundation Grants Database",
      info: "Developed a responsive, searchable grants database for the Atkinson Foundation with dynamic filtering and accessible content discovery.",
      overview:
        "A searchable grants database designed to make funding information easier to explore.",
      role: "Front-end development and WordPress implementation support.",
      highlights: [
        "Implemented dynamic filtering for grants content.",
        "Built responsive layouts for content-heavy browsing.",
        "Supported cross-browser QA and performance-minded implementation.",
      ],
      techStack: ["WordPress", "HTML", "CSS", "JavaScript", "Isotope JS"],
      link: "https://atkinsonfoundation.ca/grants-database/",
    },
    {
      id: "choosing-wisely",
      group: "earlier",
      img: ChoosingWisely,
      title: "Choosing Wisely Canada",
      info: "Enhanced a key resource for the Canadian medical community, including search and content usability improvements.",
      overview:
        "WordPress front-end support for a national medical resource website.",
      role: "Front-end development, WordPress updates and QA support.",
      highlights: [
        "Improved content discovery through search-related functionality.",
        "Worked on reliable, responsive content templates.",
        "Supported a high-trust public information website.",
      ],
      techStack: ["WordPress", "HTML", "CSS", "JavaScript", "PHP"],
      link: "https://choosingwiselycanada.org/",
    },
    {
      id: "ace-bakery",
      group: "earlier",
      img: AceBakeryImg,
      title: "ACE Bakery",
      info: "Developed and maintained consumer and foodservice website experiences, including responsive content pages and campaign-supporting updates.",
      overview:
        "WordPress development and maintenance for consumer and foodservice web properties.",
      role: "Front-end development, WordPress implementation and QA support.",
      highlights: [
        "Built content pages for foodservice audiences.",
        "Used HTML, CSS, JavaScript, PHP and jQuery in WordPress templates.",
        "Focused on responsive behavior and content maintainability.",
      ],
      techStack: ["WordPress", "HTML", "CSS", "JavaScript", "PHP", "jQuery"],
      link: "https://foodservice.acebakery.com/foodservice-restaurants/",
    },
    {
      id: "stonefire",
      group: "earlier",
      img: stonefire,
      title: "Stonefire",
      info: "Supported consumer and foodservice website updates, including regional navigation, language/location switching and responsive content pages.",
      overview:
        "WordPress front-end support across consumer and foodservice web experiences.",
      role: "Front-end development, WordPress updates, QA and responsive implementation.",
      highlights: [
        "Implemented language and location switcher functionality.",
        "Built foodservice content pages for key audience segments.",
        "Supported accessibility, navigation and responsive polish.",
      ],
      techStack: ["WordPress", "HTML", "CSS", "JavaScript", "PHP", "Flickity JS"],
      link: "https://foodservice.stonefire.com/restaurants/",
    },
    {
      id: "garden-culture",
      group: "earlier",
      img: GardenCulture,
      title: "Garden Culture Magazine",
      info: "Developed a custom video feature for Garden Culture Magazine, enabling YouTube and Vimeo embeds at the top of articles with a custom video player interface.",
      overview:
        "A WordPress media enhancement for Garden Culture Magazine that made video content easier to feature inside editorial articles.",
      role: "Front-end development, WordPress implementation, PHP updates, JavaScript behavior and QA support.",
      highlights: [
        "Built a custom video feature for article pages.",
        "Enabled YouTube and Vimeo player integration at the top of articles.",
        "Implemented a custom video player interface to support richer editorial content.",
      ],
      techStack: ["WordPress", "HTML", "CSS", "JavaScript", "PHP", "jQuery"],
      link: "https://gardenculturemagazine.com/",
    },
    {
      id: "drug-checking",
      group: "earlier",
      img: DrugChecking,
      title: "Toronto Drug Checking Service",
      info: "Contributed to a public health website refresh with clearer content structure, rebrand support and user-friendly navigation.",
      overview:
        "Website support for a public health resource focused on clear communication and access to information.",
      role: "Front-end development, WordPress content support and QA.",
      highlights: [
        "Supported content overhaul and rebrand implementation.",
        "Improved user-friendly navigation for important public health information.",
        "Worked within WordPress using HTML, CSS, JavaScript and PHP.",
      ],
      techStack: ["WordPress", "HTML", "CSS", "JavaScript", "PHP"],
      link: "https://drugchecking.community/",
    },
  ];

  return (
    <div className="App">
      <div
        className="scroll-progress"
        style={{ transform: `scaleX(${scrollProgress / 100})` }}
        aria-hidden="true"
      />
      <NavBar />
      <button
        className="mode-toggle"
        type="button"
        aria-pressed={nightMode}
        aria-label={nightMode ? "Switch to day mode" : "Switch to night mode"}
        onClick={() => setNightMode((current) => !current)}
      >
        <span className="mode-toggle__icon" aria-hidden="true">
          <span className="mode-toggle__sun" />
          <span className="mode-toggle__moon" />
        </span>
        <span className="mode-toggle__label">
          {nightMode ? "Day Mode" : "Night Mode"}
        </span>
      </button>
      <Home nightMode={nightMode} />
      <ProjectsGallery projects={projectData} />
      <Skills />
      <About />
      <Contact className="contactDesktop" />
      <Footer />
    </div>
  );
}

export default App;
